import { useState } from "react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  MicrosoftOutlookLogo, CheckCircle, WarningCircle,
  ArrowsClockwise, PaperPlaneTilt, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DEFAULT_ACK_SUBJECT = "[Ticket #{ticket_number}] We've received your request";
const DEFAULT_ACK_BODY = `Hi {name},

Thanks for reaching out to Aurora Business Systems Support. We've created ticket #{ticket_number} for you.

Subject: {subject}

Our team will get back to you shortly. You can reply to this email at any time and it will be added to your ticket.

{portal_link}

— Aurora Business Systems Support`;

export function MSGraphPanel({ settings, setSettings, msStatus, setMsStatus, isAdmin }) {
  const [testing, setTesting] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);

  return (
    <div className="hairline rounded-lg p-5 bg-white" data-testid="settings-ms365">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#4f4c90]/15 border border-[#4f4c90]/30 flex items-center justify-center">
            <MicrosoftOutlookLogo size={20} weight="duotone" className="text-[#8B87C8]" />
          </div>
          <div>
            <div className="font-heading font-semibold">Microsoft 365 shared inbox</div>
            <p className="text-xs text-slate-500 mt-1">
              Emails to this mailbox become tickets. Agent replies are sent from the same address with proper threading.
            </p>
          </div>
        </div>
        <div>
          {!msStatus?.configured && (
            <span className="chip !text-slate-600 !border-slate-300 bg-slate-100 gap-1">
              <WarningCircle size={11} weight="duotone" /> Not configured
            </span>
          )}
          {msStatus?.configured && msStatus?.ok && (
            <span className="chip !text-[#39A36E] !border-[#39A36E]/30 bg-[#39A36E]/10 gap-1">
              <CheckCircle size={11} weight="fill" /> Connected
            </span>
          )}
          {msStatus?.configured && msStatus && msStatus.ok === false && (
            <span className="chip !text-red-400 !border-red-500/30 bg-red-500/10 gap-1">
              <WarningCircle size={11} weight="fill" /> Auth error
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="bg-[#FFFFFF] border border-slate-200 rounded-md p-3">
          <div className="text-[10px] uppercase text-slate-500 tracking-wider">Mailbox</div>
          <div className="font-mono text-slate-800 mt-1 truncate">
            {msStatus?.mailbox || "support.bsys@auroraenergy.co"}
          </div>
        </div>
        <div className="bg-[#FFFFFF] border border-slate-200 rounded-md p-3">
          <div className="text-[10px] uppercase text-slate-500 tracking-wider">Last sync</div>
          <div className="font-mono text-slate-800 mt-1">
            {msStatus?.last_sync_at ? timeAgo(msStatus.last_sync_at) : "—"}
            {msStatus?.last_sync_stats && (
              <span className="text-slate-500">
                {" "}· scanned {msStatus.last_sync_stats.scanned}, created {msStatus.last_sync_stats.created}
              </span>
            )}
          </div>
        </div>
      </div>

      {msStatus?.configured && msStatus?.ok === false && msStatus?.error && (
        <div className="mt-3 text-[11px] font-mono text-red-300 bg-red-500/5 border border-red-500/20 rounded-md p-3 whitespace-pre-wrap break-all">
          {msStatus.error}
        </div>
      )}

      <div className="mt-4 space-y-3">
        <div className={`rounded-md p-3 border ${settings.email_send_enabled ? "border-[#39A36E]/25 bg-[#39A36E]/5" : "border-red-500/30 bg-red-500/5"}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                <PaperPlaneTilt size={14} weight={settings.email_send_enabled ? "fill" : "regular"} className={settings.email_send_enabled ? "text-[#39A36E]" : "text-red-400"} />
                Send outbound emails
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                {settings.email_send_enabled
                  ? "Live — the app WILL email real requesters when agents reply and when new tickets are created."
                  : "Paused — no emails will leave the mailbox. Great for testing. Agents can still 'reply' in-app but nothing is sent."}
              </div>
            </div>
            <Switch
              data-testid="settings-email-send-master"
              checked={settings.email_send_enabled}
              onCheckedChange={(v) => isAdmin && setSettings((s) => ({ ...s, email_send_enabled: v }))}
              disabled={!isAdmin}
            />
          </div>
        </div>

        <label className={`flex items-center justify-between gap-3 p-3 rounded-md border border-slate-200 bg-[#FFFFFF] ${!isAdmin ? "opacity-60" : ""}`}>
          <div>
            <div className="text-sm">Auto-ingest emails</div>
            <div className="text-[11px] text-slate-500">Poll the shared mailbox every 2 minutes and turn new emails into tickets.</div>
          </div>
          <Switch
            data-testid="settings-email-ingest"
            checked={settings.email_ingest_enabled}
            onCheckedChange={(v) => isAdmin && setSettings((s) => ({ ...s, email_ingest_enabled: v }))}
            disabled={!isAdmin}
          />
        </label>

        <div className={`rounded-md border border-slate-200 bg-[#FFFFFF] ${!isAdmin ? "opacity-60" : ""}`}>
          <label className="flex items-center justify-between gap-3 p-3 cursor-pointer">
            <div>
              <div className="text-sm">Send acknowledgement on new tickets</div>
              <div className="text-[11px] text-slate-500">Reply to the requester with a ticket number and a private link to view the ticket.</div>
            </div>
            <Switch
              data-testid="settings-email-auto-ack"
              checked={settings.email_auto_ack}
              onCheckedChange={(v) => isAdmin && setSettings((s) => ({ ...s, email_auto_ack: v }))}
              disabled={!isAdmin}
            />
          </label>

          {settings.email_auto_ack && (
            <>
              <button
                type="button"
                onClick={() => setAckOpen((v) => !v)}
                data-testid="settings-email-auto-ack-toggle-editor"
                className="w-full flex items-center gap-1 px-3 pb-2 text-[11px] text-[#4f4c90] hover:text-[#5F5CA5]"
              >
                {ackOpen ? <CaretDown size={11} /> : <CaretRight size={11} />}
                {ackOpen ? "Hide message editor" : "Customise the acknowledgement message"}
              </button>

              {ackOpen && (
                <div className="border-t border-slate-200 p-3 space-y-3 bg-slate-50/50">
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium mb-1">Subject line</div>
                    <Input
                      value={settings.email_auto_ack_subject ?? ""}
                      onChange={(e) => isAdmin && setSettings((s) => ({ ...s, email_auto_ack_subject: e.target.value }))}
                      disabled={!isAdmin}
                      placeholder={DEFAULT_ACK_SUBJECT}
                      className="h-9 bg-white border-slate-200 text-sm font-mono"
                      data-testid="settings-email-auto-ack-subject"
                    />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium mb-1">Message body</div>
                    <Textarea
                      value={settings.email_auto_ack_body ?? ""}
                      onChange={(e) => isAdmin && setSettings((s) => ({ ...s, email_auto_ack_body: e.target.value }))}
                      disabled={!isAdmin}
                      placeholder={DEFAULT_ACK_BODY}
                      className="min-h-[220px] bg-white border-slate-200 text-sm font-mono"
                      data-testid="settings-email-auto-ack-body"
                    />
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono space-y-1">
                    <div>Available placeholders (paste as-is, we&apos;ll swap them per email):</div>
                    <div className="flex flex-wrap gap-1.5">
                      {["{name}", "{ticket_number}", "{subject}", "{portal_url}", "{portal_link}"].map((p) => (
                        <code key={p} className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-800">{p}</code>
                      ))}
                    </div>
                    <div className="text-slate-400 pt-1">
                      Leave a field blank to fall back to the default template. Remember to click <span className="font-semibold text-slate-600">Save settings</span> at the top after editing.
                    </div>
                  </div>
                  {isAdmin && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setSettings((s) => ({ ...s, email_auto_ack_subject: DEFAULT_ACK_SUBJECT, email_auto_ack_body: DEFAULT_ACK_BODY }))}
                        className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                        data-testid="settings-email-auto-ack-reset"
                      >
                        Reset to default template
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <label className={`flex items-center justify-between gap-3 p-3 rounded-md border border-slate-200 bg-[#FFFFFF] ${!isAdmin ? "opacity-60" : ""}`}>
          <div>
            <div className="text-sm">Email requester on public replies by default</div>
            <div className="text-[11px] text-slate-500">When an agent posts a public comment, the &quot;Email requester&quot; checkbox is checked by default.</div>
          </div>
          <Switch
            data-testid="settings-email-reply-default"
            checked={settings.email_reply_default}
            onCheckedChange={(v) => isAdmin && setSettings((s) => ({ ...s, email_reply_default: v }))}
            disabled={!isAdmin}
          />
        </label>

        <label className={`flex items-center justify-between gap-3 p-3 rounded-md border border-slate-200 bg-[#FFFFFF] ${!isAdmin ? "opacity-60" : ""}`}>
          <div>
            <div className="text-sm">Notify assignees by email</div>
            <div className="text-[11px] text-slate-500">When a ticket is assigned to an agent, email them a summary with a direct link to the ticket.</div>
          </div>
          <Switch
            data-testid="settings-email-notify-assignee"
            checked={settings.email_notify_assignee}
            onCheckedChange={(v) => isAdmin && setSettings((s) => ({ ...s, email_notify_assignee: v }))}
            disabled={!isAdmin}
          />
        </label>

        <div className="flex items-center gap-2">
          <Button
            data-testid="settings-ms-test"
            variant="outline"
            className="border-slate-200 bg-slate-100 h-8 gap-1"
            disabled={testing || !msStatus?.configured}
            onClick={async () => {
              setTesting(true);
              try {
                const r = await api.get("/inbox/ms/status");
                setMsStatus(r.data);
                if (r.data.ok) toast.success("Connected to Microsoft 365");
                else toast.error(r.data.error?.slice(0, 200) || "Connection failed");
              } finally { setTesting(false); }
            }}
          >
            <ArrowsClockwise size={12} className={testing ? "animate-spin" : ""} /> {testing ? "Testing…" : "Test connection"}
          </Button>
        </div>
      </div>
    </div>
  );
}
